import { useState, useRef, useEffect, useCallback, type KeyboardEvent, type DragEvent, type ChangeEvent } from 'react'
import { useMessagesStore } from '@/shared/stores/messages.store'
import { useUIStore } from '@/shared/stores/ui.store'
import { useStatusStore } from '@/shared/stores/status.store'
import { useAuthStore } from '@/shared/stores/auth.store'
import { useSendQueueStore } from '@/shared/stores/send-queue.store'
import { isTurnEnded, stepFlushGate, initialFlushGate, type FlushGate } from '@/shared/stores/send-queue.flush'
import { trpc } from '@/shared/lib/trpc'
import { dbgEvent } from '@/shared/lib/debug'
import { useVoiceInput } from '@/features/voice-input'

const DRAFT_KEY = (sessionId: string) => `draft:${sessionId}`
const MAX_CONTENT_LENGTH = 50_000
const WARN_THRESHOLD = 45_000
const CRITICAL_THRESHOLD = 49_500

/** Fetches an image with Authorization header and renders it as a blob URL */
function AuthImage({ src, alt, style }: { src: string; alt: string; style?: React.CSSProperties }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)

  useEffect(() => {
    let revoked = false
    const token = useAuthStore.getState().accessToken
    fetch(src, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.blob() : null)
      .then(blob => {
        if (blob && !revoked) {
          setBlobUrl(URL.createObjectURL(blob))
        }
      })
      .catch(() => { /* ignore */ })
    return () => {
      revoked = true
      if (blobUrl) URL.revokeObjectURL(blobUrl)
    }
  }, [src]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!blobUrl) return <span style={{ ...style, display: 'inline-block', background: 'var(--color-bg-muted)' }} />
  return <img src={blobUrl} alt={alt} style={style} />
}

interface AttachedFile {
  fileId: string
  name: string
  mimeType: string
  path: string
}

export function InputBar({ sessionId, droppedFiles, onMessageSent }: { sessionId: string; droppedFiles?: FileList | null; onMessageSent?: () => void }) {
  const [content, setContent] = useState(() => localStorage.getItem(DRAFT_KEY(sessionId)) ?? '')
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isRetranscribing, setIsRetranscribing] = useState(false)

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const isStreaming = useMessagesStore(s => s.sessions[sessionId]?.isStreaming ?? false)
  const sessionStatus = useStatusStore(s => s.statuses[sessionId] ?? 'idle')
  const isAborting = sessionStatus === 'aborting'
  const canSend = !isStreaming && sessionStatus !== 'thinking' && sessionStatus !== 'active'
  // Nombre de messages empilés pour cette session (feedback UI — #10587).
  const queuedCount = useSendQueueStore(s => s.queues[sessionId]?.length ?? 0)
  const terminalMode = useUIStore(s => s.terminalMode)
  const user = useAuthStore(s => s.user)
  const isAdmin = user?.role === 'admin'

  const utils = trpc.useUtils()
  // Pattern TkDodo : optimistic update robuste avec cancel + snapshot + rollback + settled.
  // onMutate/onError/onSuccess DOIVENT être passés ici (options du hook useMutation) et non
  // au niveau de l'appel mutate() : en React Query v5, MutateOptions (options de mutate())
  // n'accepte que onSuccess/onError/onSettled — onMutate y est silencieusement ignoré.
  const sendMutation = trpc.messages.send.useMutation({
    onMutate: async (variables) => {
      const optimisticId = `optimistic-${Date.now()}`
      const optimisticMessage = {
        id: optimisticId,
        session_id: variables.sessionId,
        role: 'user' as const,
        content: variables.content,
        content_type: variables.contentType ?? 'text',
        status: 'sending' as const,
        created_at: new Date().toISOString(),
      }
      // 1. Annuler les queries en vol pour éviter qu'un re-fetch écrase l'optimistic update
      await utils.messages.list.cancel({ sessionId: variables.sessionId, limit: 50 })
      // 2. Snapshot pour rollback en cas d'erreur
      const snapshot = utils.messages.list.getData({ sessionId: variables.sessionId, limit: 50 })
      // 3. Appliquer l'optimistic update
      utils.messages.list.setData(
        { sessionId: variables.sessionId, limit: 50 },
        (old) => old ? [...old, optimisticMessage] : [optimisticMessage],
      )
      return { snapshot, optimisticId, optimisticMessage }
    },
    onError: (err, variables, context) => {
      // Rollback vers le snapshot pré-mutation
      if (context?.snapshot) {
        utils.messages.list.setData({ sessionId: variables.sessionId, limit: 50 }, context.snapshot)
      }
      console.error('Send error:', err)
    },
    onSuccess: (data, variables, context) => {
      // Remplacer l'ID optimiste par l'ID confirmé du serveur
      utils.messages.list.setData(
        { sessionId: variables.sessionId, limit: 50 },
        (old) => {
          if (!old) {
            return context?.optimisticMessage
              ? [{ ...context.optimisticMessage, id: data.messageId, status: 'complete' as const }]
              : []
          }
          return old.map(m =>
            m.id === context?.optimisticId
              ? { ...m, id: data.messageId, status: 'complete' as const }
              : m,
          )
        },
      )
    },
  })
  const abortMutation = trpc.sessions.abort.useMutation()

  const updateContent = useCallback((value: string | ((prev: string) => string)) => {
    if (typeof value === 'function') {
      setContent(prev => {
        const next = value(prev)
        const truncated = next.slice(0, MAX_CONTENT_LENGTH)
        localStorage.setItem(DRAFT_KEY(sessionId), truncated)
        return truncated
      })
    } else {
      const truncated = value.slice(0, MAX_CONTENT_LENGTH)
      setContent(truncated)
      if (truncated) {
        localStorage.setItem(DRAFT_KEY(sessionId), truncated)
      } else {
        localStorage.removeItem(DRAFT_KEY(sessionId))
      }
    }
  }, [sessionId])

  // Append transcribed voice text to the input (mic + retry paths)
  const handleTranscription = useCallback((text: string) => {
    updateContent(prev => prev + (prev ? ' ' : '') + text)
  }, [updateContent])

  const {
    isRecording,
    isTranscribing,
    recordingDuration,
    lastError: voiceError,
    canRetry,
    startRecording,
    stopRecording,
    retryTranscription,
    clearError: clearVoiceError,
  } = useVoiceInput(sessionId, handleTranscription)

  const handleRetry = useCallback(async () => {
    setIsRetranscribing(true)
    clearVoiceError()
    setUploadError(null)
    try {
      const token = useAuthStore.getState().accessToken
      const res = await fetch('/api/voice/retry', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'Retry failed' })) as { error?: string }
        throw new Error(body.error ?? `HTTP ${res.status}`)
      }
      const { text } = await res.json() as { text: string }
      if (text.trim()) {
        dbgEvent('Bus', 'emit voice:transcription:ready', { sessionId, text: text.trim() })
        handleTranscription(text.trim())
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur retry audio'
      setUploadError(message)
    } finally {
      setIsRetranscribing(false)
    }
  }, [sessionId, clearVoiceError, handleTranscription])

  // Reload draft when session changes
  useEffect(() => {
    setContent(localStorage.getItem(DRAFT_KEY(sessionId)) ?? '')
    setAttachedFiles([])
  }, [sessionId])

  // Reload draft when a voice transcription just completed (isTranscribing true -> false).
  // useVoiceInput écrit désormais le texte transcrit DIRECTEMENT dans localStorage plutôt que
  // via la closure onTranscription (point d'attention miniplan/audit #26190 : l'enregistrement
  // peut se terminer pendant que ce composant est resté monté en continu sur cette session —
  // pas de remount dans ce cas, donc rien ne relit spontanément localStorage). Ce garde
  // rattrape ce cas ; le cas "onglet quitté puis revisité" est déjà couvert par l'effet
  // ci-dessus (remount + useState initializer ligne 50).
  const wasTranscribingRef = useRef(isTranscribing)
  useEffect(() => {
    if (wasTranscribingRef.current && !isTranscribing) {
      setContent(localStorage.getItem(DRAFT_KEY(sessionId)) ?? '')
    }
    wasTranscribingRef.current = isTranscribing
  }, [isTranscribing, sessionId])

  // Clear voice error when user types
  useEffect(() => {
    if (content && canRetry) {
      clearVoiceError()
    }
  }, [content, canRetry, clearVoiceError])

  // File upload
  const uploadFile = useCallback(async (file: File) => {
    setUploadError(null)
    const formData = new FormData()
    formData.append('file', file)
    const token = useAuthStore.getState().accessToken
    try {
      const res = await fetch(`/api/files/upload?sessionId=${sessionId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'Upload failed' })) as { error?: string }
        setUploadError(body.error ?? `Erreur ${res.status}`)
        return
      }
      const data = await res.json() as { fileId: string; originalName: string; mimeType: string; path: string }
      setAttachedFiles(prev => [...prev, {
        fileId: data.fileId,
        name: data.originalName,
        mimeType: data.mimeType,
        path: data.path,
      }])
    } catch {
      setUploadError('Erreur reseau lors de l\'upload')
    }
  }, [sessionId])

  // Uploader les fichiers déposés sur la zone chat (relayés depuis ChatWidget via prop).
  // Garde par ref : chaque FileList n'est traitée qu'une fois, même si uploadFile change
  // d'identité (il dépend de sessionId) — évite le ré-upload des mêmes fichiers.
  const lastDroppedRef = useRef<FileList | null>(null)
  useEffect(() => {
    if (!droppedFiles || droppedFiles === lastDroppedRef.current) return
    lastDroppedRef.current = droppedFiles
    Array.from(droppedFiles).forEach(file => { void uploadFile(file) })
  }, [droppedFiles, uploadFile])

  const removeFile = useCallback((fileId: string) => {
    setAttachedFiles(prev => prev.filter(f => f.fileId !== fileId))
  }, [])

  const handleFileSelect = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    for (const file of files) await uploadFile(file)
    e.target.value = '' // Reset to allow re-selection
  }, [uploadFile])

  // Drag and drop
  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    const files = Array.from(e.dataTransfer.files)
    for (const file of files) await uploadFile(file)
  }, [uploadFile])

  const handleSend = useCallback(() => {
    const trimmed = content.trim()
    if (!trimmed) return
    if (trimmed.length > MAX_CONTENT_LENGTH) return

    const attachmentIds = attachedFiles.map(f => f.fileId)

    // Vider le champ + attachments immédiatement (feedback utilisateur instantané),
    // que le message parte tout de suite ou soit empilé.
    updateContent('')
    setAttachedFiles([])

    if (canSend) {
      // Tour libre → envoi immédiat (comportement historique).
      // Signal message:sent for thinking indicator
      dbgEvent('Bus', 'emit message:sent', { sessionId })
      onMessageSent?.()

      // L'update optimiste (cancel + snapshot + setData) est géré par
      // onMutate/onError/onSuccess passés aux options du hook useMutation ci-dessus.
      sendMutation.mutate({ sessionId, content: trimmed, contentType: 'text', attachmentIds })
    } else {
      // Tour en cours → empiler (FIFO par session). L'envoi ne se fait JAMAIS mid-turn :
      // le message part à la fin du tour courant via l'effet de flush ci-dessous (#10587).
      dbgEvent('Bus', 'sendQueue enqueue (turn active)', { sessionId })
      useSendQueueStore.getState().enqueue(sessionId, { content: trimmed, attachmentIds })
    }
  }, [content, canSend, sessionId, attachedFiles, sendMutation, updateContent, onMessageSent])

  // Flush de la file d'attente à la fin du tour courant (#10587).
  // La DÉCISION est portée par les fonctions pures `isTurnEnded` / `stepFlushGate`
  // (send-queue.flush.ts) — exécutées à l'identique par le harnais d'isolation.
  // Anti-burst : un seul envoi par période idle ; le message envoyé refait passer le statut à
  // active, ce qui réarme la garde pour le tour suivant.
  const flushGateRef = useRef<FlushGate>(initialFlushGate)
  useEffect(() => {
    const turnEnded = isTurnEnded(sessionStatus, isStreaming)
    const hasQueued = useSendQueueStore.getState().count(sessionId) > 0
    const { flush, gate } = stepFlushGate(flushGateRef.current, turnEnded, hasQueued)
    flushGateRef.current = gate
    if (!flush) return

    const next = useSendQueueStore.getState().shift(sessionId)
    if (!next) return // garde défensive (course impossible en pratique : shift juste après count)

    dbgEvent('Bus', 'sendQueue flush (turn ended)', { sessionId })
    onMessageSent?.()
    sendMutation.mutate({
      sessionId,
      content: next.content,
      contentType: 'text',
      attachmentIds: next.attachmentIds,
    })
  }, [sessionStatus, isStreaming, sessionId, sendMutation, onMessageSent])

  const handleEscape = () => {
    abortMutation.mutate(
      { sessionId },
      {
        onSuccess: () => {
          dbgEvent('Mutation', 'abort:success', { sessionId })
        },
        onError: (error) => {
          dbgEvent('Mutation', 'abort:error', { sessionId, error })
          console.error('Abort error:', error)
        },
      },
    )
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`
  }, [content])

  const showCharCounter = content.length > WARN_THRESHOLD
  const isCharCritical = content.length > CRITICAL_THRESHOLD

  return (
    <div
      className="input-bar-root"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        padding: 'var(--space-3) var(--space-4)',
        borderTop: '1px solid var(--color-border)',
        background: isDragging ? 'var(--color-bg-muted)' : 'var(--color-bg-surface)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        flexShrink: 0,
        transition: 'background var(--duration-fast)',
      }}
    >
      {/* Attached files preview */}
      {attachedFiles.length > 0 && (
        <div style={{
          display: 'flex',
          gap: 'var(--space-2)',
          padding: 'var(--space-1)',
          flexWrap: 'wrap',
        }}>
          {attachedFiles.map(f => (
            <div key={f.fileId} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              background: 'var(--color-bg-muted)',
              borderRadius: 'var(--radius-sm)',
              padding: '2px 6px',
              fontSize: 'var(--text-xs)',
            }}>
              {f.mimeType.startsWith('image/') ? (
                <AuthImage
                  src={f.path}
                  alt={f.name}
                  style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }}
                />
              ) : (
                <span style={{ color: 'var(--color-text-secondary)' }}>{f.name}</span>
              )}
              <button
                onClick={() => removeFile(f.fileId)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--color-text-muted)',
                  fontSize: 14,
                  padding: 0,
                  lineHeight: 1,
                }}
                aria-label={`Supprimer ${f.name}`}
              >
                x
              </button>
            </div>
          ))}
        </div>
      )}

      {/* File d'attente d'envoi — feedback minimal (#10587) */}
      {queuedCount > 0 && (
        <div style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--color-text-muted)',
          padding: '0 var(--space-1)',
          fontFamily: 'var(--font-sans)',
        }}>
          {queuedCount} message{queuedCount > 1 ? 's' : ''} en file d'attente
        </div>
      )}

      {/* Upload error */}
      {uploadError && (
        <div style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--color-error)',
          padding: '0 var(--space-1)',
        }}>
          {uploadError}
        </div>
      )}

      {/* Voice error + retry */}
      {voiceError && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          fontSize: 'var(--text-xs)',
          color: 'var(--color-error)',
          padding: '0 var(--space-1)',
        }}>
          <span>{voiceError}</span>
          {canRetry && (
            <button
              onClick={retryTranscription}
              style={{
                background: 'var(--color-bg-muted)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                padding: '2px 8px',
                fontSize: 'var(--text-xs)',
                cursor: 'pointer',
                color: 'var(--color-text-secondary)',
              }}
            >
              Reessayer
            </button>
          )}
        </div>
      )}

      {/* Drag overlay indicator */}
      {isDragging && (
        <div style={{
          textAlign: 'center',
          fontSize: 'var(--text-xs)',
          color: 'var(--color-accent)',
          padding: 'var(--space-1)',
          fontFamily: 'var(--font-sans)',
        }}>
          Deposez vos fichiers ici
        </div>
      )}

      {/* Input row */}
      <div style={{
        display: 'flex',
        gap: 'var(--space-2)',
        alignItems: 'center',
      }}>
        {/* Escape button (admin only, terminal mode) */}
        {isAdmin && terminalMode && (
          <button
            onClick={handleEscape}
            style={{
              padding: 'var(--space-2) var(--space-3)',
              background: 'var(--color-bg-muted)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--color-text-secondary)',
              fontFamily: 'var(--font-sans)',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              height: 40,
              flexShrink: 0,
            }}
            title="Envoyer ESC au terminal"
          >
            ESC
          </button>
        )}

        {/* File attach button */}
        <input ref={fileInputRef} type="file" multiple hidden onChange={handleFileSelect} />
        <button
          onClick={() => fileInputRef.current?.click()}
          style={{
            padding: 'var(--space-2)',
            background: 'none',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            color: 'var(--color-text-secondary)',
            height: 40,
            width: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
          title="Ajouter un fichier"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={content}
          onChange={e => updateContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            isStreaming
              ? "L'agent repond..."
              : 'Envoyer un message'
          }
          style={{
            flex: 1,
            resize: 'none',
            overflow: 'hidden',
            height: 40,
            minHeight: 40,
            maxHeight: 200,
            padding: 'var(--space-2) var(--space-3)',
            background: 'var(--color-bg-elevated)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-base)',
            lineHeight: 1.5,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />

        {/* Voice input button */}
        <button
          onClick={isRecording ? stopRecording : startRecording}
          disabled={isTranscribing}
          title={isRecording ? 'Cliquer pour arreter' : 'Cliquer pour parler'}
          style={{
            padding: 'var(--space-2)',
            background: isRecording ? 'var(--color-error)' : 'var(--color-bg-elevated)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            color: isRecording ? '#fff' : 'var(--color-text-secondary)',
            cursor: isTranscribing ? 'wait' : 'pointer',
            height: 40,
            width: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'background var(--duration-fast)',
          }}
        >
          {isTranscribing ? (
            <>
              <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity={0.25} />
                <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </>
          ) : isRecording ? (
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>{recordingDuration}s</span>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              {/* Corps du micro */}
              <rect x="9" y="2" width="6" height="12" rx="3" fill="currentColor" />
              {/* Arc extérieur */}
              <path d="M5 10a7 7 0 0 0 14 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
              {/* Pied vertical */}
              <line x1="12" y1="17" x2="12" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              {/* Socle horizontal */}
              <line x1="8" y1="21" x2="16" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          )}
        </button>

        {/* Retry audio button — toujours visible, retranscrit le dernier fichier audio serveur */}
        <button
          className="input-bar-retry-audio"
          onClick={() => { void handleRetry() }}
          disabled={isRetranscribing || isRecording || isTranscribing}
          title="Retranscrire le dernier enregistrement vocal"
          style={{
            padding: 'var(--space-2)',
            background: 'var(--color-bg-elevated)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            color: isRetranscribing ? 'var(--color-accent)' : (isRecording || isTranscribing) ? 'var(--color-text-muted)' : 'var(--color-text-secondary)',
            cursor: (isRetranscribing || isRecording || isTranscribing) ? 'not-allowed' : 'pointer',
            height: 40,
            width: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'color var(--duration-fast)',
          }}
        >
          {isRetranscribing ? (
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>...</span>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M1 4v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3.51 15a9 9 0 1 0 .49-4.95" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </button>

        {/* Stop button (small, secondary) — visible des que Claude est affiche occupe
            (streaming, aborting, ou statuts sans texte streame : active/thinking/initializing).
            Deblocage minimal : la file d'attente structurelle viendra plus tard. */}
        {(isStreaming || isAborting || sessionStatus === 'active' || sessionStatus === 'thinking' || sessionStatus === 'initializing') && (
          <button
            onClick={() => {
              if (!isAborting) abortMutation.mutate(
                { sessionId },
                {
                  onSuccess: () => { dbgEvent('Mutation', 'abort:success', { sessionId }) },
                  onError: (error) => {
                    dbgEvent('Mutation', 'abort:error', { sessionId, error })
                    console.error('Abort error:', error)
                  },
                },
              )
            }}
            disabled={isAborting}
            style={{
              padding: isAborting ? '6px 12px' : '0',
              background: 'var(--color-bg-muted)',
              border: isAborting ? '1px solid var(--color-border)' : '1px solid var(--color-border)',
              borderRadius: isAborting ? 'var(--radius-md)' : 'var(--radius-sm)',
              color: 'var(--color-text-secondary)',
              cursor: isAborting ? 'not-allowed' : 'pointer',
              height: 40,
              width: isAborting ? 'auto' : 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              fontSize: isAborting ? 'var(--text-sm)' : 14,
              lineHeight: 1,
              opacity: isAborting ? 0.7 : 1,
            }}
            title={isAborting ? 'Arrêt en cours...' : 'Interrompre la génération'}
          >
            {isAborting ? 'Arrêt en cours...' : '◼'}
          </button>
        )}

        {/* Send button (always visible) — n'est PLUS bloqué pendant un tour :
            si l'agent travaille, un clic empile le message (flush à la fin du tour, #10587). */}
        <button
          onClick={handleSend}
          disabled={!content.trim() || content.length > MAX_CONTENT_LENGTH}
          style={{
            padding: 'var(--space-2) var(--space-4)',
            background: !content.trim() || content.length > MAX_CONTENT_LENGTH ? 'var(--color-bg-muted)' : 'var(--color-accent)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            color: '#fff',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-sm)',
            fontWeight: 600,
            cursor: !content.trim() || content.length > MAX_CONTENT_LENGTH ? 'not-allowed' : 'pointer',
            height: 40,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          Envoyer
        </button>
      </div>

      {/* Character counter */}
      {showCharCounter && (
        <div style={{
          fontSize: 'var(--text-xs)',
          color: isCharCritical ? 'var(--color-error)' : 'var(--color-text-muted)',
          textAlign: 'right',
          padding: '0 var(--space-1)',
          fontFamily: 'var(--font-sans)',
        }}>
          {content.length.toLocaleString()} / {MAX_CONTENT_LENGTH.toLocaleString()}
        </div>
      )}
    </div>
  )
}
